import {
  BookOpen,
  Gauge,
  Inbox,
  LibraryBig,
  LogOut,
  Menu,
  Signal,
  SignalZero,
  UserRound,
  X
} from "lucide-react";
import { type PropsWithChildren, useCallback, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "../auth";
import { flushOutbox, outboxCount } from "../offline";
import { Logo } from "./Logo";

const navItems = [
  { to: "/", label: "今日", icon: Gauge, end: true },
  { to: "/library", label: "书库", icon: LibraryBig },
  { to: "/inbox", label: "待整理", icon: Inbox },
  { to: "/vocabulary", label: "词汇", icon: BookOpen }
];

export function AppShell({ children }: PropsWithChildren) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const location = useLocation();

  const sync = useCallback(async () => {
    if (!user) return;
    setOnline(navigator.onLine);
    if (navigator.onLine) await flushOutbox(user.id);
    setPending(await outboxCount(user.id));
  }, [user]);

  useEffect(() => {
    void sync();
    const foreground = () => {
      if (document.visibilityState === "visible") void sync();
    };
    window.addEventListener("online", sync);
    window.addEventListener("offline", sync);
    window.addEventListener("flussik:outbox", sync);
    document.addEventListener("visibilitychange", foreground);
    return () => {
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", sync);
      window.removeEventListener("flussik:outbox", sync);
      document.removeEventListener("visibilitychange", foreground);
    };
  }, [sync]);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${menuOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__top">
          <Logo />
          <button className="icon-button sidebar__close" onClick={() => setMenuOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar__nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__foot">
          <div className="user-chip">
            <UserRound size={18} />
            <span>
              <strong>{user?.display_name || user?.username}</strong>
              <small>{online ? "已连接" : "离线阅读"}</small>
            </span>
          </div>
          <button className="text-button" onClick={logout}>
            <LogOut size={16} />
            退出
          </button>
        </div>
      </aside>
      {menuOpen && <button className="scrim" aria-label="关闭菜单" onClick={() => setMenuOpen(false)} />}
      <div className="app-shell__main">
        <header className="mobile-header">
          <button className="icon-button" onClick={() => setMenuOpen(true)}>
            <Menu size={22} />
          </button>
          <Logo compact />
          <button className="sync-state" onClick={() => void sync()} title="点击同步">
            {online ? <Signal size={17} /> : <SignalZero size={17} />}
            {pending > 0 && <span>{pending}</span>}
          </button>
        </header>
        <main className="page">{children}</main>
        <nav className="bottom-nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
